import type { ChannelDto } from '@lagerfeuer/shared';
import { useQueryClient } from '@tanstack/react-query';
import { Archive, Lock, LockKeyhole, MessagesSquare, Plus } from 'lucide-react';
import { useId, useState } from 'react';
import { useNavigate } from 'react-router';
import { Popover, type Anchor } from '../../components/Popover';
import { errorMessage } from '../../lib/api';
import { threadsApi, type AutoArchive } from '../../lib/endpoints';
import { pathForChannel } from '../../lib/routes';
import { useAppStore } from '../../store/app';
import { can, useChannelPermissions } from '../../store/selectors';
import { useUi } from '../../store/ui';
import { absoluteTime, lastActivity, relativeTime, useLiveThread, useThreadList } from './threadUtils';
import './threads.css';

type Tab = 'active' | 'archived';

/** Aktive und archivierte Threads eines Kanals, „Thread erstellen". */
export function ThreadsPopover({ channelId, anchor, onClose }: { channelId: string; anchor: Anchor; onClose: () => void }) {
  const navigate = useNavigate();
  const [tab, setTab] = useState<Tab>('active');
  const [creating, setCreating] = useState(false);
  const [filter, setFilter] = useState('');
  const parent = useAppStore((s) => s.channels[channelId]);
  const perms = useChannelPermissions(channelId);
  const canCreate = can(perms, 'CREATE_THREADS');
  const list = useThreadList(channelId, { archived: tab === 'archived' });
  const threads = (list.data?.pages.flatMap((p) => p.threads) ?? []).filter((t) => !filter.trim() || (t.name ?? '').toLowerCase().includes(filter.trim().toLowerCase()));
  const tabsId = useId();

  const open = (t: ChannelDto) => {
    onClose();
    useUi.getState().setNavOpen(false);
    void navigate(pathForChannel(t));
  };

  return (
    <Popover anchor={anchor} onClose={onClose} placement="bottom-end" className="threads-popover" testId="threads-popover" label="Threads">
      <div className="threads-popover-head">
        <MessagesSquare size={18} aria-hidden="true" />
        <h2 className="threads-popover-title">Threads</h2>
        <span className="spacer" />
        {canCreate && !creating && (
          <button className="btn btn-sm btn-primary" onClick={() => setCreating(true)} data-testid="thread-create">
            <Plus size={14} aria-hidden="true" /> Thread erstellen
          </button>
        )}
      </div>
      {creating && parent ? (
        <CreateThreadForm parent={parent} canPrivate={parent.type === 'TEXT' && can(perms, 'CREATE_PRIVATE_THREADS')} onCancel={() => setCreating(false)} onCreated={open} />
      ) : (
        <>
          <input
            className="input threads-search"
            type="search"
            placeholder="Threads durchsuchen"
            aria-label="Threads durchsuchen"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            data-testid="threads-search"
          />
          <div className="tabs threads-tabs" role="tablist" aria-label="Thread-Ansicht">
            {(['active', 'archived'] as const).map((t) => (
              <button
                key={t}
                role="tab"
                className="tab"
                id={`${tabsId}-${t}`}
                aria-selected={tab === t}
                aria-controls={`${tabsId}-panel`}
                onClick={() => setTab(t)}
                data-testid={`threads-tab-${t}`}
              >
                {t === 'active' ? 'Aktiv' : 'Archiviert'}
              </button>
            ))}
          </div>
          <div id={`${tabsId}-panel`} role="tabpanel" aria-labelledby={`${tabsId}-${tab}`} className="threads-list scroll" data-testid="threads-list">
            {list.isLoading && (
              <div className="empty">
                <div className="spinner" />
              </div>
            )}
            {list.isError && <p className="error-text small">Threads konnten nicht geladen werden: {errorMessage(list.error)}</p>}
            {list.isSuccess && threads.length === 0 && (
              <p className="empty small" data-testid="threads-empty">
                {tab === 'active' ? 'Keine aktiven Threads.' : 'Keine archivierten Threads.'}
              </p>
            )}
            <ul role="list">
              {threads.map((t) => (
                <ThreadRow key={t.id} thread={t} onOpen={open} />
              ))}
            </ul>
            {list.hasNextPage && (
              <button className="btn btn-ghost btn-sm btn-block" disabled={list.isFetchingNextPage} onClick={() => void list.fetchNextPage()} data-testid="threads-more">
                Mehr laden
              </button>
            )}
          </div>
        </>
      )}
    </Popover>
  );
}

function ThreadRow({ thread, onOpen }: { thread: ChannelDto; onOpen: (t: ChannelDto) => void }) {
  const t = useLiveThread(thread);
  const owner = useAppStore((s) => (t.ownerId ? s.users[t.ownerId] : undefined));
  const meta = t.thread;
  const ts = lastActivity(t);
  return (
    <li>
      <button className="thread-row" onClick={() => onOpen(t)} data-testid="threads-item" data-thread-id={t.id}>
        <span className="thread-row-icon" aria-hidden="true">
          {t.type === 'PRIVATE_THREAD' ? <LockKeyhole size={16} /> : <MessagesSquare size={16} />}
        </span>
        <span className="thread-row-text">
          <span className="thread-row-name truncate">{t.name}</span>
          <span className="thread-row-meta small faint">
            {meta?.messageCount ?? 0} Nachrichten
            {owner ? ` · gestartet von ${owner.displayName || owner.username}` : ''}
            {' · '}
            <time dateTime={new Date(ts).toISOString()} title={absoluteTime(ts)}>
              {relativeTime(ts)}
            </time>
          </span>
        </span>
        {meta?.locked && <Lock size={14} aria-label="gesperrt" role="img" />}
        {meta?.archived && <Archive size={14} aria-label="archiviert" role="img" />}
      </button>
    </li>
  );
}

const ARCHIVE_OPTIONS: { value: AutoArchive; label: string }[] = [
  { value: 60, label: '1 Stunde' },
  { value: 1440, label: '24 Stunden' },
  { value: 4320, label: '3 Tage' },
  { value: 10080, label: '1 Woche' },
];

/** Formular „Thread erstellen" (Vertrag 3.4 POST /channels/:id/threads ohne Startnachricht). */
export function CreateThreadForm({
  parent,
  canPrivate,
  onCancel,
  onCreated,
}: {
  parent: ChannelDto;
  canPrivate: boolean;
  onCancel: () => void;
  onCreated: (t: ChannelDto) => void;
}) {
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [isPrivate, setPrivate] = useState(false);
  const [archive, setArchive] = useState<AutoArchive>(1440);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const id = useId();
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) return;
    setBusy(true);
    setError(null);
    try {
      const created = await threadsApi.create(parent.id, {
        name: trimmed,
        type: isPrivate ? 'PRIVATE_THREAD' : 'PUBLIC_THREAD',
        autoArchiveMinutes: archive,
      });
      const { firstMessage: _f, ...dto } = created;
      useAppStore.getState().dispatch('THREAD_CREATE', dto);
      void queryClient.invalidateQueries({ queryKey: ['threads', parent.id] });
      onCreated(dto);
    } catch (err) {
      setError(errorMessage(err));
      setBusy(false);
    }
  };
  return (
    <form className="stack thread-create-form" onSubmit={(e) => void submit(e)} data-testid="thread-create-form">
      <div className="field">
        <label htmlFor={`${id}-name`}>Thread-Name</label>
        <input
          id={`${id}-name`}
          className="input"
          value={name}
          maxLength={100}
          required
          autoFocus
          placeholder="Worum geht es?"
          onChange={(e) => setName(e.target.value)}
          data-testid="thread-create-name"
        />
      </div>
      <div className="field">
        <label htmlFor={`${id}-archive`}>Automatisch archivieren nach</label>
        <select id={`${id}-archive`} className="select" value={archive} onChange={(e) => setArchive(Number(e.target.value) as AutoArchive)} data-testid="thread-create-archive">
          {ARCHIVE_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label} Inaktivität
            </option>
          ))}
        </select>
      </div>
      {canPrivate && (
        <label className="checkbox">
          <input type="checkbox" checked={isPrivate} onChange={(e) => setPrivate(e.target.checked)} data-testid="thread-create-private" />
          <span>
            Privater Thread <span className="small faint">– nur Eingeladene und Moderierende sehen ihn</span>
          </span>
        </label>
      )}
      {error && (
        <p className="error-text small" role="alert" data-testid="thread-create-error">
          {error}
        </p>
      )}
      <div className="row thread-dialog-actions">
        <button type="button" className="btn btn-ghost btn-sm" onClick={onCancel} data-testid="thread-create-cancel">
          Abbrechen
        </button>
        <button type="submit" className="btn btn-primary btn-sm" disabled={busy || !name.trim()} data-testid="thread-create-submit">
          Erstellen
        </button>
      </div>
    </form>
  );
}
